'use client';

import { useCallback, useRef, useState, type RefObject } from 'react';
import { toast } from 'react-toastify';
import {
  SELECT_CLIENT_MODAL_SELECTION_MODE,
  type SelectClientModalSelectionMode,
} from '@/app/(main)/clients/const/clientSelection';
import Checkbox from '@/app/components/Checkbox';
import { Modal } from '@/app/components/Modal';
import { Spinner } from '@/app/components/Spinner';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import RadioButton from '@/app/components/buttons/RadioButton';
import { Search } from '@/app/components/form/Search';
import type { ClientExportScopeFilters } from '@/app/(main)/client-detail/consts/clientExportScope';
import { useClientsSearchInfiniteQuery } from '@/hooks/api/useClientsSearchInfiniteQuery';
import { useScrollToBottom } from '@/hooks/useScrollToBottom';
import { getAbbreviation } from '@/utils/getAbbreviation';
import { cn } from '@/utils/сn';
import { EXPORT_ALL_MAX_CLIENTS } from '../../const/clients';
import { clientSelectionLabels } from '../../utils/clientSelectionLabels';
import { ClientsExportLimitAlert } from '../ClientsExportLimitAlert';
import { ClientsSelectAllAction } from '../ClientsSelectAllAction';
import { SelectClientModalEmptyState } from './EmptyState';
import PlusIcon from '/public/icons/plus.svg';

const ADD_NEW_CLIENT_LABEL = 'Add New Client';

export const SELECT_CLIENT_MODAL_DEFAULT_TITLE = 'Select client';

interface SelectClientFormProps {
  isSubmitting: boolean;
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
  description?: string;
  successMessage?: string;
  errorMessage?: string;
  submitLabel?: string;
  submittingLabel?: string;
  onSelectClients: (props: { client_ids: number[] }) => Promise<void>;
  withEmail?: boolean;
  onAddNewClient?: () => void;
  selectionMode?: SelectClientModalSelectionMode;
  title?: string;
  enableSelectAllScope?: boolean;
  onSelectAllScope?: (params: { search: string }) => Promise<void> | void;
  exportListScopeFilters?: ClientExportScopeFilters;
  stackActionsOnMobile?: boolean;
}

export const SelectClientModal = ({
  onSelectClients,
  onSuccess,
  isSubmitting,
  title = SELECT_CLIENT_MODAL_DEFAULT_TITLE,
  description,
  successMessage,
  errorMessage = 'Could not complete the request',
  submitLabel = 'Continue',
  submittingLabel = 'Continuing...',
  isOpen,
  onClose,
  withEmail,
  selectionMode = SELECT_CLIENT_MODAL_SELECTION_MODE.SINGLE,
  onAddNewClient,
  enableSelectAllScope = false,
  onSelectAllScope,
  exportListScopeFilters,
  stackActionsOnMobile = false,
}: SelectClientFormProps) => {
  const [search, setSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(() => new Set());
  const [isAllScopeSelected, setIsAllScopeSelected] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const isMultipleSelection = selectionMode === SELECT_CLIENT_MODAL_SELECTION_MODE.MULTIPLE;

  const { clients, total, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, debouncedSearch } =
    useClientsSearchInfiniteQuery({
      search,
      withEmail,
      agent_id: exportListScopeFilters?.agent_id,
      status: exportListScopeFilters?.status,
      fallbackSearch: exportListScopeFilters?.name,
    });

  const hasExportScopeFilters = Boolean(
    exportListScopeFilters?.agent_id != null ||
      exportListScopeFilters?.status ||
      exportListScopeFilters?.name?.trim()
  );

  const isQueryEnabled = debouncedSearch.trim().length > 0 || hasExportScopeFilters;
  const showSelectAllScope = isMultipleSelection && enableSelectAllScope;
  const isOverExportLimit = total > EXPORT_ALL_MAX_CLIENTS;
  const isExportBlocked = isAllScopeSelected && isOverExportLimit;
  const isEverythingSelected =
    (isAllScopeSelected && total > 0) || (total > 0 && selectedClientIds.size >= total);
  const canSelectAllScope = total > 0;

  const handleSearchChange = useCallback((value: string) => {
    setIsAllScopeSelected(false);
    setSearch(value);
  }, []);

  const selectAllScope = useCallback(() => {
    if (total <= 0) return;
    setIsAllScopeSelected(true);
  }, [total]);

  const clearScopeSelection = useCallback(() => {
    setIsAllScopeSelected(false);
    setSelectedClientIds(new Set());
  }, []);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  useScrollToBottom({
    ref: listRef as RefObject<HTMLElement>,
    hasNextPage: Boolean(hasNextPage),
    isFetching: isFetchingNextPage,
    fetchNextPage: handleLoadMore,
  });

  const toggleClientSelection = useCallback((clientId: number) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  }, []);

  const handleContinue = useCallback(async () => {
    if (isAllScopeSelected) {
      if (!onSelectAllScope || isOverExportLimit || total <= 0) return;
      try {
        await onSelectAllScope({ search: debouncedSearch });
        onSuccess();
      } catch {
        toast.error(errorMessage);
      }
      return;
    }

    const clientIds = isMultipleSelection
      ? Array.from(selectedClientIds)
      : selectedClientId != null
        ? [selectedClientId]
        : [];

    if (clientIds.length === 0) return;

    try {
      await onSelectClients({ client_ids: clientIds });
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess();
    } catch {
      toast.error(errorMessage);
    }
  }, [
    debouncedSearch,
    errorMessage,
    isAllScopeSelected,
    isMultipleSelection,
    isOverExportLimit,
    onSelectAllScope,
    onSelectClients,
    onSuccess,
    selectedClientId,
    selectedClientIds,
    successMessage,
    total,
  ]);

  const hasSelection =
    (isAllScopeSelected && total > 0) ||
    (isMultipleSelection ? selectedClientIds.size > 0 : selectedClientId != null);

  const showEmpty = isQueryEnabled && !isLoading && !isFetching && clients.length === 0;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="flex flex-col w-[calc(100vw-2rem)] min-w-0 max-w-[550px] max-h-[calc(100dvh-2rem)] md:w-full md:min-w-[550px] md:max-h-none md:min-h-[660px]">
        <div className="px-4 pt-8 pb-5 flex flex-col gap-0.5 items-center text-center md:px-9 md:pt-11.5 md:pb-8.5">
          <h2 className="light text-blue-900">{title}</h2>
          {description && <p className="text-body-regular text-grey-700 max-w-[480px]">{description}</p>}
        </div>

        {withEmail && (
          <div className="mx-4 mb-2 rounded-lg bg-blue-50 px-4 py-3 md:mx-9">
            <p className="text-body-small text-blue-900">
              Clients will not appear if they do not have an email address registered. Please add an email to send
              forms.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <Search
            className="px-4 md:px-9"
            name="client-search"
            placeholder="Search clients"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
          />

          {showSelectAllScope ? (
            <div className="flex flex-col gap-2 px-4 md:px-9">
              <div className="flex items-center justify-between gap-3 max-md:gap-2.5">
                <span className="font-inter text-base font-medium text-black max-md:text-body-small">
                  {clientSelectionLabels.modal({
                    selectedCount: selectedClientIds.size,
                    total,
                    isAllSelected: isEverythingSelected,
                  })}
                </span>
                {isLoading ? (
                  <div className="h-5 w-40 animate-pulse rounded bg-grey-200" aria-hidden />
                ) : canSelectAllScope ? (
                  <ClientsSelectAllAction
                    isAllSelected={isEverythingSelected}
                    total={total}
                    onSelectAll={selectAllScope}
                    onClearSelection={clearScopeSelection}
                  />
                ) : null}
              </div>
              {isExportBlocked ? (
                <ClientsExportLimitAlert maxClients={EXPORT_ALL_MAX_CLIENTS} hasFilters={hasExportScopeFilters} />
              ) : null}
            </div>
          ) : null}

          {onAddNewClient && isQueryEnabled && (
            <BaseButton
              type="button"
              intent="ghost_semibold"
              size="ghost_small"
              startIcon={<PlusIcon />}
              onClick={onAddNewClient}
              disabled={isSubmitting}
              className="self-start mx-4 md:mx-9"
            >
              {ADD_NEW_CLIENT_LABEL}
            </BaseButton>
          )}

          <div ref={listRef} className="flex flex-col overflow-y-auto min-h-0 flex-1 md:flex-initial md:max-h-[320px]">
            {(isLoading || isFetching) && (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            )}

            {showEmpty && <SelectClientModalEmptyState />}
            {clients.map((client, index) => {
              const isSelected = isMultipleSelection
                ? selectedClientIds.has(client.id)
                : selectedClientId === client.id;
              const isRowChecked = isSelected || (isMultipleSelection && isAllScopeSelected);
              const avatarSrc = client.profile_photo_url ?? undefined;
              const rowClassName = cn(
                'flex items-center justify-between gap-2 px-4 py-3 transition-colors md:px-9',
                isAllScopeSelected ? 'cursor-not-allowed' : 'cursor-pointer',
                isRowChecked && 'bg-blue-50',
                !isRowChecked && !isAllScopeSelected && 'hover:bg-grey-100'
              );

              const rowContent = (
                <>
                  <div
                    className="h-10 w-10 rounded-full overflow-hidden bg-grey-300 border-grey-300 shrink-0 flex items-center justify-center text-sm font-medium text-grey-700 bg-center bg-cover"
                    style={avatarSrc ? { backgroundImage: `url(${avatarSrc})` } : undefined}
                  >
                    {!avatarSrc ? (
                      <span className="text-body-regular text-grey-700">{getAbbreviation(client.full_name)}</span>
                    ) : null}
                  </div>
                  <span className="flex-1 min-w-0 line-clamp-2 break-all text-left font-inter text-base text-black">
                    {client.full_name}
                  </span>
                  {isMultipleSelection ? (
                    <span
                      className="shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        size="xsmall"
                        variant="secondary"
                        checked={isRowChecked}
                        disabled={isAllScopeSelected}
                        onChange={() => toggleClientSelection(client.id)}
                        aria-label={`Select ${client.full_name}`}
                      />
                    </span>
                  ) : (
                    <RadioButton
                      name="selected-client"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedClientId(client.id);
                      }}
                    />
                  )}
                </>
              );

              return (
                <div key={`${client.id}-${index}`} className="flex flex-col gap-2">
                  {isMultipleSelection ? (
                    <div
                      role="checkbox"
                      aria-checked={isRowChecked}
                      aria-label={`Select ${client.full_name}`}
                      tabIndex={0}
                      className={rowClassName}
                      onClick={() => {
                        if (!isAllScopeSelected) toggleClientSelection(client.id);
                      }}
                      onKeyDown={(e) => {
                        if (isAllScopeSelected) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleClientSelection(client.id);
                        }
                      }}
                    >
                      {rowContent}
                    </div>
                  ) : (
                    <label className={rowClassName}>{rowContent}</label>
                  )}
                </div>
              );
            })}

            {isFetchingNextPage && (
              <div className="flex justify-center">
                <Spinner />
              </div>
            )}
          </div>
        </div>

        <div
          className={cn(
            'px-4 pt-5 pb-8 flex items-center justify-between gap-4 mt-auto md:gap-5 md:px-9 md:pt-8.5 md:pb-11.5',
            stackActionsOnMobile && 'max-[450px]:flex-col max-[450px]:items-stretch max-[450px]:gap-3'
          )}
        >
          {onAddNewClient ? (
            <BaseButton
              type="button"
              intent="secondary"
              size="full"
              startIcon={<PlusIcon />}
              onClick={onAddNewClient}
              disabled={isSubmitting}
            >
              {ADD_NEW_CLIENT_LABEL}
            </BaseButton>
          ) : (
            <BaseButton type="button" intent="secondary" size="full" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </BaseButton>
          )}
          <BaseButton
            type="button"
            intent="primary"
            size="full"
            onClick={() => void handleContinue()}
            disabled={!hasSelection || isSubmitting || isExportBlocked}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </BaseButton>
        </div>
      </div>
    </Modal>
  );
};
